/**
 *  payplus/soapClient.js is intended to serve as a Node.js wrapper for the PayPlus SOAP Client
 *  for storing, retrieving and making payments with credit/debit cards
 *
 *  Available Methods:
 *
 *  - AuthCreditCardFull
 *  - AuthCreditCardFullEx
 *  - RetryAuthCreditCardFull
 *  - RemoveLastTransaction
 *  - RemoveTransactionsByVoucherId
 *  - RestoreAuthCreditCardFullByVoucherId
 *  - GetDepositData
 *  - RetainerCreditCardStorage
 *  - DepositTransactionsEx
 */
const camelcaseKeys 	        = require('camelcase-keys');
const moment 			        = require('moment');
const soap 				        = require('soap');
const Joi                       = require('joi');
const transactionResultCodes    = require('./errors/transactions.json');
const retainerResultCodes       = require('./errors/retainer.json');
const utils                     = require('./utils');

const wsUrlSandbox              = 'https://ws.payplus.co.il/pptest/ws/payplus/service.asmx?WSDL';
const wsUrlProduction           = 'https://ws.payplus.co.il/pp/ws/payplus/service.asmx?WSDL';
const wsUrl                     = process.env.NODE_ENV === 'production' ? wsUrlProduction : wsUrlSandbox;

class InvalidParametersError extends Error {
    constructor(message) {
        super(message);
        this.name = 'InvalidParametersError';
    }
}

class PayPlusError extends Error {
    constructor(message) {
        super(message);
        this.name = 'PayPlusError';
    }
}

function validate(object, schema) {
    const result = Joi.validate(object, schema);
    if(result.error) {
        let err = result.error;
        let message;
        if(err.details.length) {
            message = err.details[0].message;
        } else {
            message = err.message;
        }
        return new InvalidParametersError(message);
    }

    return null;
}

function getFormattedResponseForAuthCard(result) {

    let formattedIntOp = utils.getIntOp(result.resultRecord);
    formattedIntOp.transactionRecord = result.transactionRecord;
    let retainerResult = result.valueEx3;

    function retainer(start, end) {
        return retainerResult.substring(start, end);
    }

    // ValueEx3 returned a non empty string from the retainer function
    let retainerStatusCode, retainerCardLast4, retainerCardExpDate;
    if(retainerResult && retainerResult.length) {
        retainerStatusCode = retainer(0, 3);
        retainerCardLast4 = retainer(3, 7);
        retainerCardExpDate = retainer(7, 11);
    }

    if(retainerStatusCode > 0 /* Retainer Error */) {
        let errorMessage = retainerResultCodes[retainerStatusCode.toString()];
        return {
            status: retainerStatusCode,
            retainerError: errorMessage
        };
    }

    return formattedIntOp;
}

/**
 * Constructs a new PayPlusClient from a given SOAP client
 * @param soapClient A SOAP client, generated using soap for node.js
 * @param {object} ppOptions An options object. must contain: user, password, cashierNumber, merchantNumber
 * @constructor
 */
function PayPlusClient(soapClient, ppOptions) {
    this.client = soapClient;
    this.user = ppOptions.user;
    this.password = ppOptions.password;
    this.cashierNumber = ppOptions.cashierNumber;
    this.merchantNumber = ppOptions.merchantNumber;
}

// Options:
// - cvv2
// - id
// - token
// - amount (In Agorot)
// - expanded
// - protectRetry
PayPlusClient.prototype.authCreditCardEx = function(options, callback) {

    let user = this.user,
        password = this.password,
        cashierNum = this.cashierNumber,
        merchantNum = this.merchantNumber;

    const validationSchema = {
        token: Joi.string().guid().required(),
        amount: Joi.number().min(100).required(),
        paramJ: Joi.number().valid(2, 4).required(), // Currently only support these, expand later if needed
        cvv2: Joi.string().min(3).max(4).optional(),
        id: Joi.string().length(9).optional() // Standard Israeli ID (Tehudat Zehut)
    };

    let err = validate(options, validationSchema);
    if(err) {
        return callback(err);
    }

    let date = moment();
    let transactionDate_yyyyMMdd = date.format('YYYYMMDD');
    let transactionTime_HHmm = date.format('HHmm');
    let requestCardKey = `K${options.token}`; // Per PayPlus documentation on making payment with GUID token

    let args = {
        isUseRetry : options.protectRetry ? '1' : '0',
        premiumUserName : user,
        premiumPassword : password,
        premiumCashierNum : cashierNum,
        merchantNumber : merchantNum,
        transactionDate_yyyyMMdd : transactionDate_yyyyMMdd,
        transactionTime_HHmm : transactionTime_HHmm,
        cardNum : '',
        expDate_YYMM : '',
        amount : options.amount,
        transactionType : '01',
        creditTerms : '1',
        currency : '1',
        code : '51',
        paramJ : (options.paramJ|| '2').toString(), // Default behavior is to not process the transaction, only check the payment details
        uniqueTransactionNumber_SixDigits: '',
        // Unused parameters
        cochavAmount : '',
        authNum: '',
        cvv2 : (options.cvv2 || '0').toString(),
        id : (options.id || '000000000').toString(),
        track2 : requestCardKey,
        firstAmount : '',
        nonFirstAmount : '',
        numOfPayment : '',
        sapakMutav : '',
        sapakMutavNo : '',
        uniqNum : '',
        clubCode : '',
        addData : '',
        eci : '',
        cavvUcaf : '',
        last4Digits : '',
        transactionCurrency : '',
        transactionAmount : '',
    };

    this.client.AuthCreditCardFullEx(args, function(err, result) {
        if(err) return callback(err, 0);

        result = getFormattedResponseForAuthCard(camelcaseKeys(result));
        let responseCode = result.status;
        let resultMessage = transactionResultCodes[responseCode];

        // ResponeCode != 0, or is not a warning (041-047) => There was an error
        if(responseCode && (responseCode < 41 || responseCode > 47)) {
            return callback(new PayPlusError(resultMessage), responseCode);
        }

        callback(null, result);
    }, { timeout: 60000 });
};

PayPlusClient.prototype.issuePayment = function(options, callback) {
    options.paramJ = 4;

    this.authCreditCardEx(options, callback);
};

PayPlusClient.prototype.checkPayment = function(options, callback) {
    options.paramJ = 2; // TODO Check if this is the best approach

    this.authCreditCardEx(options, callback);
};


/**
 * Request a credit card transaction
 * @param options An options object
 * @param callback A callback function (err, result)
 * @param action The retainer action, must be one of the following: ADD, EDIT, GET, DEL
 */

PayPlusClient.prototype.retainerAction = function(action, options, callback) {

    let ACTION_ADD = 'ADD',
        ACTION_EDIT = 'EDIT',
        ACTION_GET = 'GET',
        ACTION_DELETE = 'DEL';

    // `PayPlus Card Retainer action must be one of the following: ${validActions.join()}`

    const schema = {
        options: {
            expMonth: (action === ACTION_ADD || action === ACTION_EDIT) ? Joi.number().min(1).max(12).required() : Joi.any().forbidden(), // TODO remove if action = GET, DELETE
            expYear: (action === ACTION_ADD || action === ACTION_EDIT) ? Joi.number().min(1).required() : Joi.any().forbidden(), // TODO remove if action = GET, DELETE
            cardNumber: (action === ACTION_ADD) ? Joi.string().creditCard().required() : Joi.any().forbidden(),
            cardToken: (action === ACTION_GET || action === ACTION_EDIT || action === ACTION_DELETE) ? Joi.string().guid().required() : Joi.any().forbidden()
        },
        action: Joi.string().required().valid(ACTION_ADD, ACTION_EDIT, ACTION_GET, ACTION_DELETE)
    };

    const err = validate({
        options: options,
        action: action
    }, schema);

    if(err) {
        return callback(err);
    }

    let args = {
        premiumUserName : this.user,
        premiumPassword : this.password,
        premiumCashierNum : this.cashierNumber,
        merchantNumber : this.merchantNumber,
        actionType : action,
        cardOrKey : options.cardNumber ? utils.padCreditCard(options.cardNumber) : options.cardToken
    };

    if(action === ACTION_ADD || action === ACTION_EDIT) {
        // Adding and editing card requires expMonth and expYear
        let expDate = moment();
        // TODO FIX
        expDate.set('month', parseInt(options.expMonth) - 1);
        expDate.set('year', options.expYear.toString().length === 2 ? parseInt(`20${options.expYear}`) : parseInt(options.expYear));
        args.expDate_MMYY = expDate.format('MMYY');
    } else {
        // Deleting and retrieving card does not require expMonth and expYear
        args.expDate_MMYY = '';
    }

    this.client.RetainerCreditCardStorage(args, function(err, result) {
        if(err) return callback(err, null);

        // Reformat result
        result = camelcaseKeys(result);
        let resultCode = parseInt(result['retainerCreditCardStorageResult']);

        // Success
        if(resultCode === 0) {

            let resultCard = result['resultCard'],
                resultExpDate = result['resultExpDate'],
                resultKey = result['resultKey'],
                resultExpMonth = resultExpDate.substring(0, 2),
                resultExpYear = resultExpDate.substring(2, 4);

            switch (action) {
                case ACTION_GET: return callback(null, {
                        action: action,
                        last4: resultCard.substring(resultCard.length - 4, resultCard.length),
                        type: utils.getCardType(utils.removeCardPadding(resultCard)),
                        expMonth: resultExpMonth,
                        expYear: resultExpYear
                    });
                case ACTION_ADD: return callback(null, {
                        action: action,
                        token: resultKey
                    });
                case ACTION_EDIT: return callback(null, {
                        action: action
                    });
                case ACTION_DELETE: return callback(null, {
                        action: action
                    });
            }
        } else {
            let errorMessage = retainerResultCodes[resultCode.toString()];
            callback(new PayPlusError(errorMessage));
        }

    });
};

/**
 * Store a credit card for retainer
 * @param cardNumber Card Number
 * @param expMonth Card's Expiration Month
 * @param expYear Card's Expiration Year
 * @param callback A callback function(err, result)
 */
PayPlusClient.prototype.storeCard = function(cardNumber, expMonth, expYear, callback) {
    let options = {
        cardNumber: cardNumber,
        expMonth: expMonth,
        expYear: expYear
    };
    this.retainerAction('ADD', options, callback);
};

/**
 * Retrieve retained card details
 * @param cardToken Retained card GUID token
 * @param callback A callback function(err, result)
 * @suppress unused
 */
PayPlusClient.prototype.getCard = function(cardToken, callback) {
    let options = {
        cardToken: cardToken
    };
    this.retainerAction('GET', options, callback);
};

/**
 * Edit a retained card's expiration date
 * @param cardToken Retained card GUID token
 * @param expMonth Expiration Month (MM)
 * @param expYear Expiration Year (YY)
 * @param callback A callback function(err, result)
 */
PayPlusClient.prototype.editCard = function(cardToken, expMonth, expYear, callback) {
    let options = {
        cardToken: cardToken,
        expMonth: expMonth,
        expYear: expYear
    };
    this.retainerAction('EDIT', options, callback);
};

/**
 * Delete a retained card
 * @param cardToken Retained card GUID token
 * @param callback A callback function(err, result)
 */
PayPlusClient.prototype.deleteCard = function(cardToken, callback) {
    let options = {
        cardToken: cardToken
    };
    this.retainerAction('DEL', options, callback);
};

/**
 * Initialize a new PayPlus client
 * @param {object} options Options object that must contain: username, password, cashierNum, merchantNum
 * as provided by PayPlus
 * @param {function} callback
 */
module.exports = function(options, callback) {

    const validationSchema = {
        username: Joi.string().required(),
        password: Joi.string().required(),
        cashierNum: Joi.string().required(),
        merchantNum: Joi.string().required()
    };

    const err = validate(options, validationSchema);
    if(err) {
        callback(err);
    } else {
        soap.createClient(wsUrl, function(err, client) {

            if(err) return callback(err);

            return callback(null, new PayPlusClient(client, {
                user: options.username,
                password: options.password,
                cashierNumber: options.cashierNum,
                merchantNumber: options.merchantNum
            }));
        });
    }
};